import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [provider, setProvider] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // Update axios headers when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token, api]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setProvider(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setProvider(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, provider: providerData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setProvider(providerData);
    return providerData;
  };

  const register = async (email, password, companyName) => {
    const response = await api.post('/auth/register', { 
      email, 
      password, 
      company_name: companyName 
    });
    const { access_token, provider: providerData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setProvider(providerData);
    return providerData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setProvider(null);
  };

  const value = {
    provider,
    token,
    loading,
    login,
    register,
    logout,
    api,
    isAuthenticated: !!token && !!provider
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
